import mixpanel from "mixpanel-browser";
import { Config } from "@/configs/config";

function getUtmFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const utm_source = params.get("utm_source") || "";
  const utm_content = params.get("utm_content") || "";

  return Object.fromEntries(
    Object.entries({ utm_source, utm_content }).filter(([, v]) => v !== "")
  );
}

function getReferrerSnapshot() {
  const referrer = document.referrer || "";
  if (!referrer) {
    return {
      initial_referrer: "direct",
      initial_referring_domain: "direct",
    };
  }

  try {
    return {
      initial_referrer: referrer,
      initial_referring_domain: new URL(referrer).hostname || "direct",
    };
  } catch {
    return {
      initial_referrer: referrer,
      initial_referring_domain: "direct",
    };
  }
}

function getAttributionSnapshot() {
  return {
    ...getReferrerSnapshot(),
    ...getUtmFromUrl(),
  };
}

function normalizeDistinctId(value) {
  if (value === null || value === undefined) return null;
  const stringValue = String(value).trim();
  return stringValue.length ? stringValue : null;
}

export function initMixpanel() {
  if (!Config.MIXPANEL_TOKEN) return;
  console.log(Config.MIXPANEL_TOKEN);

  mixpanel.init(Config.MIXPANEL_TOKEN, {
    autocapture: true,
    record_sessions_percent: 100,
    api_host: Config.MIXPANEL_API_HOST,
    persistence: "localStorage",
    // Sends track requests immediately (reduces perceived delay for click tracking).
    batch_requests: false,
    debug: process.env.NODE_ENV !== "production",
  });

  const attribution = getAttributionSnapshot();

  // Always send one explicit event so it's obvious the custom tracking is live.
  mixpanel.track("App Loaded", {
    $current_url: window.location.href,
    ...attribution,
  });
}

export function track(eventName, properties = {}) {
  if (!Config.MIXPANEL_TOKEN) return;
  const attribution = getAttributionSnapshot();

  mixpanel.track(eventName, {
    $current_url: window.location.href,
    ...attribution,
    ...properties,
  });
}

export function trackAndFlush(eventName, properties = {}) {
  track(eventName, properties);
  try {
    mixpanel.flush?.();
  } catch {
    // ignore
  }
}

export function trackPageView(toRoute, fromRoute) {
  track("Page Viewed", {
    page_name: String(toRoute?.name || toRoute?.path || "unknown"),
    page_path: toRoute?.fullPath || toRoute?.path || window.location.pathname,
    referrer_path: fromRoute?.fullPath || fromRoute?.path || "",
  });
}

export function identifyUser(userLike) {
  if (!Config.MIXPANEL_TOKEN) return;

  const distinctId =
    normalizeDistinctId(userLike?.user_id) ||
    normalizeDistinctId(userLike?.id) ||
    normalizeDistinctId(userLike?.userId);

  if (!distinctId) return;

  mixpanel.identify(distinctId);

  // Mixpanel's "Users" page only shows users who have a Profile (People).
  // Creating/updating a profile here makes them appear there.
  const email = userLike?.address || userLike?.email;
  const name = userLike?.full_name || userLike?.name;
  const profileProps = {
    ...(email ? { $email: email } : {}),
    ...(name ? { $name: name } : {}),
  };

  // Always set at least one profile field so the user appears under Data -> Users.
  mixpanel.people.set({
    last_seen_at: new Date().toISOString(),
    ...profileProps,
  });
}

export function aliasAndIdentifyUserId(userId) {
  if (!Config.MIXPANEL_TOKEN) return;
  const distinctId = normalizeDistinctId(userId);
  if (!distinctId) return;

  try {
    mixpanel.alias(distinctId);
  } catch {
    // ignore (alias can only be called once)
  }

  mixpanel.identify(distinctId);

  mixpanel.people.set({
    last_seen_at: new Date().toISOString(),
  });
}

export function resetMixpanel() {
  if (!Config.MIXPANEL_TOKEN) return;
  mixpanel.reset();
}
